import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { jobAPI, uploadAPI } from '../../services/api';
import {
    FaArrowLeft, FaBriefcase, FaMapMarkerAlt, FaDollarSign,
    FaCalendarAlt, FaBuilding, FaGlobe, FaFilePdf, FaUpload,
    FaCheckCircle, FaExclamationTriangle
} from 'react-icons/fa';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/Button';
import { LoadingSpinner, EmptyState } from '../../components/dashboard';

const JobDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [job, setJob] = useState(null);
    const [loading, setLoading] = useState(true);
    const [applying, setApplying] = useState(false);
    const [showApplyForm, setShowApplyForm] = useState(false);
    const [resume, setResume] = useState(null);
    const [coverLetter, setCoverLetter] = useState('');
    const [hasApplied, setHasApplied] = useState(false);

    useEffect(() => {
        loadJob();
    }, [id]);

    const loadJob = async () => {
        try {
            setLoading(true);
            const res = await jobAPI.getById(id);
            if (res.success) {
                setJob(res.job);
            } else {
                toast.error('Job not found');
                navigate('/jobs');
            }
        } catch (err) {
            console.error('Failed to load job:', err);
            toast.error('Failed to load job details');
            navigate('/jobs');
        } finally {
            setLoading(false);
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                toast.error('File size must be less than 5MB');
                return;
            }
            if (!['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'].includes(file.type)) {
                toast.error('Only PDF and Word documents are allowed');
                return;
            }
            setResume(file);
        }
    };

    const handleApply = async (e) => {
        e.preventDefault();

        if (!resume) {
            toast.error('Please upload your resume');
            return;
        }

        setApplying(true);
        try {
            // 1. Upload resume
            const uploadRes = await uploadAPI.uploadFile(resume);
            const resumePath = uploadRes.filename;

            // 2. Submit application
            await jobAPI.apply(id, {
                resume_path: resumePath,
                cover_letter: coverLetter
            });

            toast.success('Application submitted successfully!');
            setHasApplied(true);
            setShowApplyForm(false);
            loadJob(); // Reload to update application count (though findById doesn't return count currently, but good practice)
        } catch (err) {
            console.error('Failed to submit application:', err);
            toast.error(err.message || 'Failed to submit application');
        } finally {
            setApplying(false);
        }
    };

    if (loading) return <LoadingSpinner />;

    if (!job) return <EmptyState message="Job not found" />;

    const isAuthorizedToApply = ['student', 'alumni'].includes(user?.role);

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-4xl mx-auto px-6 py-8">
                <Link to="/jobs" className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors">
                    <FaArrowLeft className="text-sm" />
                    <span className="font-medium">Back to Job Board</span>
                </Link>

                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
                    {/* Header */}
                    <div className="p-8 border-b border-gray-100 bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                            <div>
                                <h1 className="text-3xl font-bold mb-2">{job.title}</h1>
                                <div className="flex flex-wrap gap-4 text-blue-50">
                                    <div className="flex items-center gap-2">
                                        <FaBuilding />
                                        <span>{job.company_name}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <FaMapMarkerAlt />
                                        <span>{job.location}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <FaBriefcase />
                                        <span>{job.job_type.replace('_', ' ')}</span>
                                    </div>
                                </div>
                            </div>
                            {!hasApplied && isAuthorizedToApply && !showApplyForm && (
                                <button
                                    onClick={() => setShowApplyForm(true)}
                                    className="px-8 py-3 bg-white text-blue-600 rounded-lg font-bold hover:bg-blue-50 transition-colors shadow-lg"
                                >
                                    Apply Now
                                </button>
                            )}
                            {hasApplied && (
                                <div className="flex items-center gap-2 bg-green-500/20 px-4 py-2 rounded-lg border border-green-400/30">
                                    <FaCheckCircle />
                                    <span className="font-bold">Applied</span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Details */}
                    <div className="p-8">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
                            <div className="bg-gray-50 p-4 rounded-lg border border-gray-100 text-center">
                                <FaDollarSign className="text-blue-600 mx-auto text-xl mb-2" />
                                <p className="text-xs text-gray-500 uppercase font-bold tracking-wider">Salary Range</p>
                                <p className="font-semibold text-gray-900">{job.salary_range || 'Competitive'}</p>
                            </div>
                            <div className="bg-gray-50 p-4 rounded-lg border border-gray-100 text-center">
                                <FaCalendarAlt className="text-blue-600 mx-auto text-xl mb-2" />
                                <p className="text-xs text-gray-500 uppercase font-bold tracking-wider">Deadline</p>
                                <p className="font-semibold text-gray-900">
                                    {job.application_deadline ? new Date(job.application_deadline).toLocaleDateString() : 'Rolling'}
                                </p>
                            </div>
                            <div className="bg-gray-50 p-4 rounded-lg border border-gray-100 text-center">
                                <FaGlobe className="text-blue-600 mx-auto text-xl mb-2" />
                                <p className="text-xs text-gray-500 uppercase font-bold tracking-wider">Work Type</p>
                                <p className="font-semibold text-gray-900">{job.is_remote ? 'Remote' : 'On-site'}</p>
                            </div>
                        </div>

                        <div className="space-y-8">
                            <div>
                                <h2 className="text-xl font-bold text-gray-900 mb-4 border-b pb-2">Job Description</h2>
                                <div className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                                    {job.description}
                                </div>
                            </div>

                            <div>
                                <h2 className="text-xl font-bold text-gray-900 mb-4 border-b pb-2">Requirements</h2>
                                <div className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                                    {job.requirements}
                                </div>
                            </div>

                            <div>
                                <h2 className="text-xl font-bold text-gray-900 mb-4 border-b pb-2">About {job.company_name}</h2>
                                <div className="text-gray-700 leading-relaxed">
                                    {job.company_description || 'No description provided.'}
                                    {job.company_website && (
                                        <div className="mt-4">
                                            <a
                                                href={job.company_website.startsWith('http') ? job.company_website : `https://${job.company_website}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-blue-600 hover:underline inline-flex items-center gap-2"
                                            >
                                                Visit Website <FaArrowLeft className="rotate-180 text-xs" />
                                            </a>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Apply Form */}
                {showApplyForm && (
                    <div className="bg-white rounded-xl shadow-lg border border-blue-200 overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-500">
                        <div className="p-6 bg-blue-50 border-b border-blue-100 flex justify-between items-center">
                            <h2 className="text-xl font-bold text-blue-900 flex items-center gap-2">
                                <FaBriefcase /> Apply for this position
                            </h2>
                            <button
                                onClick={() => setShowApplyForm(false)}
                                className="text-blue-600 hover:text-blue-800"
                            >
                                Cancel
                            </button>
                        </div>
                        <form onSubmit={handleApply} className="p-8 space-y-6">
                            {!isAuthorizedToApply && (
                                <div className="bg-red-50 border border-red-200 p-4 rounded-lg flex items-start gap-3">
                                    <FaExclamationTriangle className="text-red-500 mt-1" />
                                    <div>
                                        <p className="text-red-800 font-medium">Restricted Access</p>
                                        <p className="text-red-700 text-sm">Only students and alumni can apply for jobs. Please contact support if you believe this is an error.</p>
                                    </div>
                                </div>
                            )}

                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-2">Resume (PDF or Word) *</label>
                                <div className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors ${resume ? 'border-green-300 bg-green-50' : 'border-gray-200 hover:border-blue-300 bg-gray-50'}`}>
                                    <input
                                        type="file"
                                        onChange={handleFileChange}
                                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                        accept=".pdf,.doc,.docx"
                                        disabled={!isAuthorizedToApply || applying}
                                    />
                                    {resume ? (
                                        <div className="flex flex-col items-center">
                                            <FaFilePdf className="text-4xl text-green-600 mb-2" />
                                            <p className="text-gray-900 font-medium">{resume.name}</p>
                                            <p className="text-gray-500 text-xs">{(resume.size / 1024 / 1024).toFixed(2)} MB</p>
                                            <button type="button" onClick={(e) => { e.stopPropagation(); setResume(null); }} className="mt-2 text-sm text-red-600 hover:underline">Remove</button>
                                        </div>
                                    ) : (
                                        <div className="flex flex-col items-center">
                                            <FaUpload className="text-4xl text-gray-400 mb-2" />
                                            <p className="text-gray-600 font-medium">Click or drag to upload resume</p>
                                            <p className="text-gray-400 text-xs">PDF or Word, max 5MB</p>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-2">Cover Letter (Optional)</label>
                                <textarea
                                    value={coverLetter}
                                    onChange={(e) => setCoverLetter(e.target.value)}
                                    rows={5}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="Briefly explain why you're a good fit for this role..."
                                    disabled={!isAuthorizedToApply || applying}
                                />
                            </div>

                            <Button
                                type="submit"
                                variant="primary-gradient"
                                className="w-full py-4 text-lg"
                                loading={applying}
                                disabled={!isAuthorizedToApply || applying || !resume}
                            >
                                Submit Application
                            </Button>
                        </form>
                    </div>
                )}
            </div>
        </div>
    );
};

export default JobDetail;
